import queryStorefrontApi from '../../../../../../scripts/utils/queryStorefrontApi';

interface IContextualPriceData {
  [productId: string]: {
    price: {
      amount: string;
      currencyCode: string;
    };
    compareAtPrice: {
      amount: string;
      currencyCode: string;
    } | null;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
      maxVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    tags: string[];
  };
}

/**
 * Fetch contextual prices AND TAGS for a list of product IDs using the Storefront API
 * Uses the @inContext directive to get prices for the specific country
 *
 * @param productIds List of product IDs (numeric strings, e.g. "7271234567890")
 * @returns Map of product ID to price and tag data
 */
const fetchContextualPrices = async (productIds: string[]): Promise<IContextualPriceData> => {
  if (!productIds || productIds.length === 0) {
    return {};
  }

  // Get country code from config if available (it was added to theme.liquid)
  let countryCode = 'GB';
  try {
    const configStr = document.querySelector('[data-storefront-api-config]')?.textContent;
    if (configStr) {
      const config = JSON.parse(configStr);
      if (config.country) {
        countryCode = config.country;
      }
    }
  } catch (e) {
    console.warn('Could not read Storefront API config for country code, defaulting to GB');
  }

  // Build query alias for each product to batch requests
  // IDs need to be formatted as GIDs: gid://shopify/Product/123456789
  const queryParts = productIds.map((id, index) => {
    const gid = `gid://shopify/Product/${id}`;
    return `
      product_${index}: product(id: "${gid}") {
        id
        tags
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 1) {
          nodes {
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
    `;
  });

  const query = `
    query ContextualPrices @inContext(country: ${countryCode}) {
      ${queryParts.join('\n')}
    }
  `;

  try {
    const response: any = await queryStorefrontApi(query);

    if (!response || !response.data) {
      console.error('Contextual price query failed:', response);
      return {};
    }

    const resultMap: IContextualPriceData = {};

    productIds.forEach((id, index) => {
      const productData = response.data[`product_${index}`];
      if (productData) {
        const firstVariant = productData.variants?.nodes?.[0];
        resultMap[id] = {
          price: firstVariant?.price,
          compareAtPrice: firstVariant?.compareAtPrice,
          priceRange: productData.priceRange,
          tags: productData.tags || []
        };
      }
    });

    return resultMap;
  } catch (error) {
    console.error('Error fetching contextual prices:', error);
    return {};
  }
};

export default fetchContextualPrices;
